import { FC, useEffect, useState, useCallback } from "react";
import {
  Button,
  color,
  InputGroup,
  InputSelect,
  PhoneInputGroup,
} from "@funded-here-interface/common";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import {
  CountryCode,
  countries,
  countryNameToCodeMap,
} from "@funded-here-interface/common/src/constant/country";
import "react-toastify/dist/ReactToastify.css";
import { NO_REGEX } from "@funded-here-interface/common";
import { Industry, LegalStructure, IndustryGroup } from "../../constants/enums";
import {
  IndustryGroupMap,
  LegalStructures,
} from "../../constants/dropdownValues";
import { camelCaseToNormalCase } from "../../Utils/formatter";
import { toast } from "react-toastify";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import { useStyles } from "../../styles/BizProfileCss";
import { initializeBusinessDetails } from "../../Utils/dispatchBorrower";
import { formatDateDraft } from "@funded-here-interface/common/src/Utils/formatter";
import { isValidPhoneNumber } from "react-phone-number-input";

const initialFormState = {
  companyName: "",
  registrationNumber: "",
  companyStartDate: "",
  legalStructure: "",
  legalStructureOthers: "",
  industryGroup: "",
  industryDropdownValues: [] as Industry[],
  institutionType: "",
  industryOthers: "",
  countryOfIncorporation: "",
  countryOfDomicile: "",
  address: "",
  postalcode: "",
  companyWebsite: "",
  financialTurnover: "",
  phoneNumber: "",
};

const initialErrorState = Object.keys(initialFormState).reduce((acc, key) => {
  acc[key] = "";
  return acc;
}, {} as Record<string, string>);

const BorrowerBizProfile: FC = () => {
  const Navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const borBiz = useSelector((state: RootState) => state.borBiz);
  const userId = useSelector((state: RootState) => state.auth.userId);
  const { token } = useSelector((state: RootState) => state.auth);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  const [form, setForm] = useState({ ...initialFormState });
  const [error, setError] = useState({ ...initialErrorState });
  const [isOtherLegalStructure, setIsOtherLegalStructure] = useState(false);
  const [countryCode, setCountryCode] = useState<CountryCode | undefined>();
  const [initPage, setInitPage] = useState(true);

  // Populate form from draft
  const populateFormFromDraft = useCallback((data: any) => {
    setForm({
      companyName: data.companyName || "",
      registrationNumber: data.companyRegistrationNo || "",
      companyStartDate: formatDateDraft(data.companyStartDate) || "",
      legalStructure: data.legalStructure || "",
      legalStructureOthers: data.legalStructureOthers || "",
      industryGroup: data.industryGroup || "",
      industryDropdownValues:
        IndustryGroupMap[data.industryGroup as IndustryGroup] || [],
      institutionType: data.industry || "",
      industryOthers: data.industryOthers || "",
      countryOfIncorporation: data.countryOfIncorporation || "",
      countryOfDomicile: data.countryOfDomicile || "",
      address: data.legalEntityAddress || "",
      postalcode: data.postalCode?.toString() || "",
      companyWebsite: data.website || "",
      financialTurnover:
        Number(data.previousYearClosingInventory).toString() || "",
      phoneNumber: data.phoneNumber || "",
    });
    setIsOtherLegalStructure(data.legalStructure === LegalStructure.OTHERS);
    if (data.countryOfDomicile) {
      const code = countryNameToCodeMap.get(data.countryOfDomicile);
      setCountryCode(code as CountryCode);
    }
  }, []);

  // Fetch draft on mount
  useEffect(() => {
    const initializeDraft = async () => {
      if (initPage) {
        try {
          const data = await getOrgDraftMutation.mutateAsync({
            userId: parseInt(userId),
            token,
          });
          if (data) {
            populateFormFromDraft(data);
          }
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
        }
      }
    };
    initializeDraft();
  }, []);

  // Generic change handler
  const handleFieldChange = (field: keyof typeof form, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError((prev) => ({ ...prev, [field]: "" }));
  };

  // Special handlers
  const handleLegalStructureChange = (value: LegalStructure) => {
    setForm((prev) => ({
      ...prev,
      legalStructure: value,
      legalStructureOthers: "",
    }));
    setIsOtherLegalStructure(value === LegalStructure.OTHERS);
    setError((prev) => ({
      ...prev,
      legalStructure: "",
      legalStructureOthers: "",
    }));
  };

  const handleIndustryGroupChange = (value: IndustryGroup) => {
    setForm((prev) => ({
      ...prev,
      industryGroup: value,
      industryDropdownValues: IndustryGroupMap[value] || [],
      institutionType: "",
    }));
    setError((prev) => ({
      ...prev,
      industryGroup: "",
      institutionType: "",
    }));
  };

  const handleCountryOfDomicileChange = (value: string) => {
    setForm((prev) => ({ ...prev, countryOfDomicile: value }));
    setError((prev) => ({ ...prev, countryOfDomicile: "" }));
    const code = countryNameToCodeMap.get(value);
    setCountryCode(code as CountryCode);
  };

  // Validation
  const validateForm = () => {
    let valid = true;
    const customError: typeof error = { ...initialErrorState };

    Object.entries(form).forEach(([key, value]) => {
      if (
        key !== "legalStructureOthers" &&
        key !== "institutionType" &&
        key !== "industryOthers" &&
        !value
      ) {
        valid = false;
        customError[key] = `${camelCaseToNormalCase(key)} is required`;
      }
    });

    if (isOtherLegalStructure && !form.legalStructureOthers) {
      valid = false;
      customError.legalStructureOthers =
        "Legal Structure Specification is required";
    }
    if (!form.institutionType) {
      valid = false;
      customError.institutionType = "Industry is required";
    }

    if (!isValidPhoneNumber(String(form.phoneNumber))) {
      valid = false;
      customError.phoneNumber = "Phone Number is not valid";
    }
    setError(customError);
    return valid;
  };

  // Save draft
  const saveDraft = (showToast: boolean = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token,
        companyName: form.companyName,
        companyRegistrationNo: form.registrationNumber,
        companyStartDate: form.companyStartDate || null,
        legalStructure: form.legalStructure,
        countryOfIncorporation: form.countryOfIncorporation,
        countryOfDomicile: form.countryOfDomicile,
        legalEntityAddress: form.address,
        postalCode: form.postalcode,
        website: form.companyWebsite,
        phoneNumber: form.phoneNumber || null,
        industryGroup: form.industryGroup,
        industry: form.institutionType,
        previousYearClosingInventory: form.financialTurnover || null,
      },
      {
        onSuccess: () => {
          if (showToast) toast.success("Draft Saved");
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  // Submit handler
  const handleSubmit = () => {
    if (validateForm()) {
      saveDraft(false);
      initializeBusinessDetails(dispatch, form);

      Navigate("/bor-rep");
    }
  };

  return (
    <div style={{ marginTop: "70px" }}>
      <InputGroup
        isErrored={error.companyName}
        onChange={(value: string) => handleFieldChange("companyName", value)}
        labelText="Company Name"
        id="companyName"
        value={form.companyName}
      />
      <InputGroup
        isErrored={error.registrationNumber}
        onChange={(value: string) =>
          handleFieldChange("registrationNumber", value)
        }
        labelText="Registration Number"
        id="registrationNumber"
        value={form.registrationNumber}
      />
      <InputGroup
        isErrored={error.incorporationDate}
        onChange={(value: any) => handleFieldChange("companyStartDate", value)}
        labelText="Date of Incorporation"
        value={form.companyStartDate}
        id="incorporationDate"
        type="date"
      />
      <InputSelect
        isErrored={error.legalStructure}
        onChange={handleLegalStructureChange}
        labelText="Legal Structure"
        id="legalStructure"
        value={form.legalStructure}
        data={LegalStructures}
      />
      {isOtherLegalStructure && (
        <InputGroup
          isErrored={error.legalStructureOthers}
          onChange={(value: string) =>
            handleFieldChange("legalStructureOthers", value)
          }
          labelText="Please Specify Legal Structure"
          id="legalStructureOthers"
          value={form.legalStructureOthers}
        />
      )}
      <InputSelect
        isErrored={error.industryGroup}
        onChange={handleIndustryGroupChange}
        labelText="Industry Group"
        value={form.industryGroup}
        id="industryGroup"
        data={Object.values(IndustryGroup).sort((a, b) => a.localeCompare(b))}
      />
      <InputSelect
        isErrored={error.institutionType}
        onChange={(value: Industry) =>
          handleFieldChange("institutionType", value)
        }
        labelText="Industry"
        value={form.institutionType}
        id="institutionType"
        data={[...form.industryDropdownValues].sort((a, b) =>
          a.localeCompare(b)
        )}
      />
      <InputSelect
        isErrored={error.countryOfIncorporation}
        onChange={(value: string) =>
          handleFieldChange("countryOfIncorporation", value)
        }
        labelText="Country of Incorporation"
        id="country"
        value={form.countryOfIncorporation}
        data={countries}
      />
      <InputSelect
        isErrored={error.countryOfDomicile}
        onChange={handleCountryOfDomicileChange}
        labelText="Country of Domicile"
        id="countryOfDomicile"
        value={form.countryOfDomicile}
        data={countries}
      />
      <InputGroup
        isErrored={error.address}
        onChange={(value: string) => handleFieldChange("address", value)}
        labelText="Address"
        id="address"
        value={form.address}
      />
      <InputGroup
        isErrored={error.postalcode}
        onChange={(value: string) =>
          handleFieldChange("postalcode", value.replace(NO_REGEX, ""))
        }
        labelText="Postal Code"
        id="postalcode"
        value={form.postalcode}
      />
      <InputGroup
        isErrored={error.companyWebsite}
        onChange={(value: string) => handleFieldChange("companyWebsite", value)}
        labelText="Company Website / Online Marketplace Link"
        id="companyWebsite"
        value={form.companyWebsite}
      />
      <InputGroup
        isErrored={error.financialTurnover}
        onChange={(value: string) =>
          handleFieldChange("financialTurnover", value.replace(NO_REGEX, ""))
        }
        labelText="Previous year closing inventory amount (as per Balance sheet)"
        id="financialTurnover"
        value={form.financialTurnover}
      />
      <div style={{ marginBottom: "20px" }}>
        <PhoneInputGroup
          isErrored={error.phoneNumber}
          value={form.phoneNumber}
          country={countryCode}
          onChange={(value: string) => handleFieldChange("phoneNumber", value)}
        />
      </div>
      <Button
        onClick={handleSubmit}
        textColor="#FFFF"
        backgroundColor="#338F86"
        width="100%"
      >
        Next
      </Button>
      <div className={classes.buttonWrapper}>
        <Button
          onClick={() => saveDraft()}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          width="100%"
        >
          Save Draft
        </Button>
      </div>
    </div>
  );
};

export default BorrowerBizProfile;
